import { useEffect, useRef, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { api, isMock } from '../api/client';
import { requireSession } from '../lib/auth';
import { Countdown, ErrorNotice } from '@bounded/ui/state';
import { Button, Field, Input } from '@bounded/ui/primitives';
import { AuthLayout } from '@bounded/ui/auth-layout';
import { useT } from '../i18n';

/** Codes are 16 hex characters; people paste them with spaces or dashes. */
const normalize = (value: string) => value.replace(/[^0-9a-f]/gi, '').toUpperCase().slice(0, 16);
const grouped = (code: string) => code.match(/.{1,4}/g)?.join(' ') ?? '';
const CMD = <code>bounded pair</code>;

export const Route = createFileRoute('/pair')({
  validateSearch: (s): { code?: string } => ({ code: typeof s.code === 'string' ? normalize(s.code) || undefined : undefined }),
  beforeLoad: ({ context, location }) => requireSession(context.queryClient, location.href),
  component: Pair,
});

/**
 * Deliberately outside the app shell: one task, no navigation to wander into.
 * The user must compare the code with their own terminal before approving;
 * a code someone else sent them is the phishing path this page guards.
 */
function Pair() {
  const t = useT(); const m = t.pair;
  const { code: fromUrl } = Route.useSearch();
  const [code, setCode] = useState(fromUrl ?? (isMock ? 'A1B2C3D4E5F60718' : ''));
  const inspect = useMutation({ mutationFn: () => api.inspectPairing(code) });
  const approve = useMutation({ mutationFn: () => api.approvePairing(code) });
  // Arriving from the CLI's approve_url: look the code up straight away. Looking up is read-only;
  // pairing still needs the explicit button after comparing the code with the terminal.
  const looked = useRef(false);
  useEffect(() => { if (fromUrl?.length === 16 && !looked.current) { looked.current = true; inspect.mutate(); } }, [fromUrl, inspect]);

  return (
    <AuthLayout title={m.title}>
      {approve.data ? (
        <div className="grid gap-3">
          <p className="text-lg">{m.done(inspect.data?.name)}</p>
          <p className="text-graphite">{m.doneBody}</p>
          <Link to="/devices/$deviceId" params={{ deviceId: approve.data.device_id }} className="text-boundary hover:underline">{m.openDevice}</Link>
        </div>
      ) : !inspect.data ? (
        <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); inspect.mutate(); }}>
          <p className="text-graphite">{m.intro(CMD)}</p>
          {inspect.error && <ErrorNotice error={inspect.error} />}
          <Field label={m.code} htmlFor="code">
            <Input id="code" autoComplete="off" spellCheck={false} className="h-11 font-mono text-lg tracking-[0.15em]" value={grouped(code)} onChange={(e) => setCode(normalize(e.target.value))} placeholder="XXXX XXXX XXXX XXXX" />
          </Field>
          <Button type="submit" variant="primary" disabled={code.length !== 16 || inspect.isPending}>{m.continue}</Button>
        </form>
      ) : (
        <div className="grid gap-5">
          <div className="rounded-[var(--radius-panel)] border border-rule bg-surface p-4">
            <p className="text-graphite">{m.deviceName}</p>
            {/* Device-chosen name: plain text. */}
            <p className="text-lg font-medium">{inspect.data.name}</p>
            <p className="mt-3 text-graphite">{m.codeLabel}</p>
            <p className="font-mono text-lg tracking-[0.15em]">{grouped(code)}</p>
            <p className="mt-3 text-sm"><Countdown until={inspect.data.expires_at} /></p>
          </div>
          <p>{m.warning(CMD)}</p>
          {inspect.data.claimed && <p role="alert" className="text-deny">{m.claimed(CMD)}</p>}
          {approve.error && <ErrorNotice error={approve.error} />}
          <div className="flex gap-2">
            <Button variant="primary" disabled={approve.isPending || inspect.data.claimed} onClick={() => approve.mutate()}>{m.approve(inspect.data.name)}</Button>
            <Button variant="ghost" onClick={() => inspect.reset()}>{m.differentCode}</Button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
}
